/**
 * 商品カタログ ⇔ 報酬設定セット の連動切替を促す共有ダイアログ (v3.2.20)。
 *
 * 2 方向に使える:
 *  - `direction='catalog-to-commission'`: 商品カタログ A を「適用中」にする操作の後、
 *      「一緒に報酬設定セットも切り替えますか？」と聞く。
 *      → リストは commission_sets。1 件選択 (or 無視)。選択したセットを activate。
 *  - `direction='commission-to-catalog'`: 報酬設定セット A を「有効化」する操作の後、
 *      「一緒に商品カタログも切り替えますか？」と聞く。
 *      → リストは ProductCategory ごとに紐づく ProductCatalog 群を表示。
 *        ブランド (catalog.category) ごとに 1 件選択 (or 無視)。選択された各 catalog を activate。
 *
 * 設計方針 (Q3 = 案 Y):
 *  - 連鎖の無限ループを防ぐため、このダイアログから activate 呼び出しを行う際は、
 *    呼び出し元の「primary 切替」は既に終わっている前提。ダイアログ側で API を直叩きする。
 *  - サーバ側 (Cloud Function) は touch しない。連携は UI 層に閉じる。
 */
import type { ProductCatalog } from '@goenhub/shared';
import { Check, Coins, Package } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import {
  useActivateCommissionSet,
  useCommissionSets,
  type CommissionSetRecord,
} from '@/hooks/useCommissions';
import {
  useActivateProductCatalog,
  useProductCatalogs,
  type ProductCatalogRecord,
} from '@/hooks/useProductCatalogs';

export type PairDialogDirection =
  | 'catalog-to-commission'
  | 'commission-to-catalog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  direction: PairDialogDirection;
  /** 直前に primary 切替が完了したエンティティ ID (catalog または commission_set)。表示用ヒント */
  primaryEntityLabel?: string;
}

export function CatalogCommissionPairDialog({
  open,
  onOpenChange,
  direction,
  primaryEntityLabel,
}: Props) {
  if (direction === 'catalog-to-commission') {
    return (
      <CatalogToCommissionDialog
        open={open}
        onOpenChange={onOpenChange}
        primaryEntityLabel={primaryEntityLabel}
      />
    );
  }
  return (
    <CommissionToCatalogDialog
      open={open}
      onOpenChange={onOpenChange}
      primaryEntityLabel={primaryEntityLabel}
    />
  );
}

// ─────────────────────────────────────────────
// 方向 1: 商品カタログ切替 → 報酬設定セット連動
// ─────────────────────────────────────────────

function CatalogToCommissionDialog({
  open,
  onOpenChange,
  primaryEntityLabel,
}: Omit<Props, 'direction'>) {
  const sets = useCommissionSets();
  const activate = useActivateCommissionSet();
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const currentActive = useMemo(
    () => (sets.data ?? []).find((s) => s.isActive) ?? null,
    [sets.data],
  );

  const handleConfirm = async () => {
    if (!selectedSetId) {
      // 無視 = 何もせず閉じる (Q4)
      onOpenChange(false);
      return;
    }
    if (selectedSetId === currentActive?.id) {
      // 既に有効化中のセットを選んだ場合も何もせず閉じる
      onOpenChange(false);
      return;
    }
    try {
      await activate.mutateAsync({ setId: selectedSetId });
      toast.success('報酬設定セットを切り替えました');
      onOpenChange(false);
    } catch (err) {
      toast.error(`切替失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="size-4 text-[var(--accent-amber,_#d4a373)]" />
            一緒に報酬設定も切り替えますか？
          </DialogTitle>
          {primaryEntityLabel && (
            <div className="text-[11px] text-[var(--text-muted)] mt-1">
              商品カタログ「{primaryEntityLabel}」を適用中にしました。
              <br />
              現在有効な報酬設定:{' '}
              <span className="font-medium text-[var(--text-secondary)]">
                {currentActive?.name ?? '(無効)'}
              </span>
            </div>
          )}
        </DialogHeader>

        {sets.isLoading ? (
          <Spinner />
        ) : (sets.data ?? []).length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] py-4 text-center">
            報酬設定セットがまだありません。
          </p>
        ) : (
          <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
            {(sets.data ?? []).map((set) => (
              <SetRow
                key={set.id}
                set={set}
                selected={selectedSetId === set.id}
                isCurrentActive={set.id === currentActive?.id}
                onClick={() =>
                  setSelectedSetId(selectedSetId === set.id ? null : set.id)
                }
              />
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            無視して閉じる
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={activate.isPending || !selectedSetId}
          >
            {activate.isPending ? '切替中…' : '選択したセットに切替'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SetRow({
  set,
  selected,
  isCurrentActive,
  onClick,
}: {
  set: CommissionSetRecord;
  selected: boolean;
  isCurrentActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-md border transition-colors flex items-start gap-3 ${
        selected
          ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
          : 'border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]'
      }`}
    >
      <div
        className={`flex-shrink-0 mt-0.5 size-4 rounded-full border-2 flex items-center justify-center ${
          selected
            ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
            : 'border-[var(--border)]'
        }`}
      >
        {selected && <Check className="size-2.5" strokeWidth={3} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
          {set.name}
          {isCurrentActive && (
            <span className="text-[10px] text-[var(--accent-amber,_#d4a373)] font-medium">
              ★ 現在有効
            </span>
          )}
        </div>
        {set.description && (
          <p className="text-[11px] text-[var(--text-muted)] mt-0.5 line-clamp-1">
            {set.description}
          </p>
        )}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────
// 方向 2: 報酬設定セット切替 → 商品カタログ連動
// ─────────────────────────────────────────────
//
// ブランド (catalog.category) ごとにカタログ一覧を表示し、各ブランドにつき 1 件選択 (or 無視)。
// 「無視」されたブランドは触らない (既存 active のまま)。
//
// 「カテゴリーごとに」と仕様文にあるが、商品カタログ自体は「ブランド名 (category 文字列)」で
// グルーピング管理されている (ProductCategory は別概念)。仕様文の「カテゴリー」はブランドの意。
function CommissionToCatalogDialog({
  open,
  onOpenChange,
  primaryEntityLabel,
}: Omit<Props, 'direction'>) {
  const catalogs = useProductCatalogs();
  const activate = useActivateProductCatalog();
  // ブランド (category) ごとの「選択された catalogId」マップ。null = そのブランドは無視
  const [selections, setSelections] = useState<Record<string, string | null>>({});

  const groupedByBrand = useMemo(() => {
    const map = new Map<string, ProductCatalogRecord[]>();
    for (const c of catalogs.data ?? []) {
      const arr = map.get(c.category) ?? [];
      arr.push(c);
      map.set(c.category, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalogs.data]);

  const handleSelect = (brand: string, catalogId: string | null) => {
    setSelections((s) => ({ ...s, [brand]: catalogId }));
  };

  const handleConfirm = async () => {
    // 選択されたカタログのうち、現在 active でないものだけ activate を呼ぶ
    const tasks: Promise<unknown>[] = [];
    const activatedLabels: string[] = [];
    for (const [, list] of groupedByBrand) {
      const targetId = selections[list[0].category];
      if (!targetId) continue; // 無視
      const target = list.find((c) => c.id === targetId);
      if (!target) continue;
      if (target.isActive) continue; // 既に active なら呼ぶ必要なし
      tasks.push(activate.mutateAsync(targetId));
      activatedLabels.push(`${target.category}/${target.label || target.version}`);
    }
    if (tasks.length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await Promise.all(tasks);
      toast.success(
        `${tasks.length} ブランドの商品カタログを切り替えました\n${activatedLabels.join(', ')}`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`一部の切替に失敗: ${(err as Error).message}`);
    }
  };

  const hasAnySelection = Object.values(selections).some((v) => !!v);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="size-4 text-[var(--accent)]" />
            一緒に商品カタログも切り替えますか？
          </DialogTitle>
          {primaryEntityLabel && (
            <div className="text-[11px] text-[var(--text-muted)] mt-1">
              報酬設定「{primaryEntityLabel}」を有効化しました。
              ブランドごとに切替先カタログを選択してください（無視可）。
            </div>
          )}
        </DialogHeader>

        {catalogs.isLoading ? (
          <Spinner />
        ) : groupedByBrand.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] py-4 text-center">
            商品カタログがまだありません。
          </p>
        ) : (
          <div className="space-y-4 max-h-[420px] overflow-y-auto">
            {groupedByBrand.map(([brand, list]) => {
              const currentActive = list.find((c) => c.isActive) ?? null;
              const selectedId = selections[brand] ?? null;
              return (
                <div
                  key={brand}
                  className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                      <Package className="size-3.5 text-[var(--accent)]" />
                      {brand}
                      <span className="text-[10px] text-[var(--text-muted)] font-normal">
                        {list.length} バージョン
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSelect(brand, null)}
                      className={`text-[11px] px-2 py-0.5 rounded transition-colors ${
                        selectedId === null
                          ? 'bg-[var(--bg-hover)] text-[var(--text-secondary)]'
                          : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                      }`}
                    >
                      このブランドを無視
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {list.map((c) => (
                      <CatalogRow
                        key={c.id}
                        catalog={c}
                        selected={selectedId === c.id}
                        isCurrentActive={c.id === currentActive?.id}
                        onClick={() => handleSelect(brand, c.id)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            無視して閉じる
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={activate.isPending || !hasAnySelection}
          >
            {activate.isPending ? '切替中…' : '選択したカタログに切替'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogRow({
  catalog,
  selected,
  isCurrentActive,
  onClick,
}: {
  catalog: ProductCatalog & { id: string };
  selected: boolean;
  isCurrentActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-2.5 py-1.5 rounded-md border transition-colors flex items-center gap-2 ${
        selected
          ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
          : 'border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]'
      }`}
    >
      <div
        className={`flex-shrink-0 size-4 rounded-full border-2 flex items-center justify-center ${
          selected
            ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
            : 'border-[var(--border)]'
        }`}
      >
        {selected && <Check className="size-2.5" strokeWidth={3} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-[var(--text-primary)] flex items-center gap-2">
          {catalog.label || catalog.version}
          {isCurrentActive && (
            <span className="text-[10px] text-[var(--accent-green)] font-medium">
              ● 適用中
            </span>
          )}
        </div>
        <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
          {catalog.version} · {catalog.itemCount} 件
        </div>
      </div>
    </button>
  );
}
